import React, { ChangeEvent, FC, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import InfoText from 'components/shared/info-text.component';
import StatusDatePicker from 'components/orders/order-details/status-date-picker.component';
import BatchSkippedOrders from 'components/orders/shared/batch-skipped-orders.component';
import { batchSendOrdersPreviewTemplate, batchSendOrdersAsExternalUserTemplate } from 'apollo/templates/order';
import { BatchOrdersResult, BatchSendOrdersPreview, ExternalUserInvoiceAction } from 'models/order';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type ExternalUserBatchSendToClinicDialogProps = {
    open: boolean;
    orderIds: string[];
    onClose: () => void;
    onDone: (result: BatchOrdersResult) => void;
};

/**
 * DD-3391: the batch "Send to clinic" for an elevated external user.
 *
 * Its own dialog for the same reason the single-order one is: the per-order
 * invoicing section is not theirs to answer, so it is absent rather than
 * disabled, and the approver is the signed-in user rather than a pick from the
 * lab's technician list. Everything else - one date, production marked finished,
 * the compiled delivery-note PDF - works as it does for the lab.
 */
const ExternalUserBatchSendToClinicDialog: FC<ExternalUserBatchSendToClinicDialogProps> = ({
    open,
    orderIds,
    onClose,
    onDone,
}: ExternalUserBatchSendToClinicDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'invoices']);
    const { showBoundary } = useDenthubErrorBoundary();
    const currentUser = currentUserVar();
    const [date, setDate] = useState<Date | null>(new Date());
    const [isArchive, setIsArchive] = useState(false);
    const [compileDeliveryNotes, setCompileDeliveryNotes] = useState(true);
    const [collectiveInvoiceAction, setCollectiveInvoiceAction] = useState<ExternalUserInvoiceAction>(
        ExternalUserInvoiceAction.READY_FOR_INVOICE,
    );

    const { data, loading } = useQuery<{ batchSendOrdersPreview: BatchSendOrdersPreview }>(
        batchSendOrdersPreviewTemplate,
        {
            variables: { orderIds },
            fetchPolicy: 'network-only',
            skip: !open,
            onError(error) {
                showBoundary(error);
            },
        },
    );
    const preview = data?.batchSendOrdersPreview;

    const counts = useMemo(() => {
        const byGroup = { COLLECTIVE: 0, PER_ORDER: 0, NO_INVOICE: 0 };
        preview?.eligible.forEach((order) => {
            byGroup[order.invoiceGroup] += 1;
        });
        return byGroup;
    }, [preview]);
    const eligibleCount = preview?.eligible.length ?? 0;

    const [batchSendOrdersAsExternalUser, { loading: running }] = useMutation<{
        batchSendOrdersAsExternalUser: BatchOrdersResult;
    }>(batchSendOrdersAsExternalUserTemplate, {
        onCompleted: ({ batchSendOrdersAsExternalUser: result }) => {
            onDone(result);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleConfirm = () => {
        if (!preview) {
            return;
        }
        batchSendOrdersAsExternalUser({
            variables: {
                input: {
                    orderIds: preview.eligible.map((order) => order._id),
                    approvedBy: { fullName: currentUser.fullName, id: currentUser._id || null },
                    date,
                    isArchive,
                    collectiveInvoiceAction,
                    compileDeliveryNotes,
                },
            },
        });
    };

    return (
        <DHDialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            title={translate('orders:BATCH_SEND_TO_CLINIC')}
            actions={
                <>
                    {eligibleCount ? (
                        <FormControl margin="dense" fullWidth>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={isArchive}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                            setIsArchive(event.target.checked)
                                        }
                                    />
                                }
                                label={translate('orders:ARCHIVE_THE_ORDERS')}
                                data-test="external-user-batch-send-to-clinic-dialog.archive-checkbox"
                            />
                        </FormControl>
                    ) : undefined}
                    <Button onClick={onClose}>{translate('common:CANCEL')}</Button>
                    {eligibleCount ? (
                        <LoadingButton
                            sx={{ minWidth: 220 }}
                            variant="contained"
                            onClick={handleConfirm}
                            loading={running}
                            data-test="external-user-batch-send-to-clinic-dialog.confirm"
                        >
                            {translate('orders:BATCH_SEND_ORDERS_BUTTON', { count: eligibleCount })}
                        </LoadingButton>
                    ) : undefined}
                </>
            }
        >
            {loading || !preview ? (
                <Loader />
            ) : !eligibleCount ? (
                <Box>
                    <Alert severity="info">{translate('orders:BATCH_NO_ELIGIBLE_ORDERS')}</Alert>
                    <BatchSkippedOrders skipped={preview.skipped} />
                </Box>
            ) : (
                <Box>
                    <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                        {translate('orders:BATCH_SEND_TO_CLINIC_INFO', { count: eligibleCount })}
                    </Alert>
                    <BatchSkippedOrders skipped={preview.skipped} />

                    <Box mt={2}>
                        <Typography variant="h4" mb={1}>
                            {translate('orders:APPROVED_BY')}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    fullWidth
                                    disabled
                                    margin="dense"
                                    value={currentUser.fullName || ''}
                                    label={translate('common:NAME')}
                                    data-test="external-user-batch-send-to-clinic-dialog.approved-by"
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <StatusDatePicker onChangeDate={setDate} />
                            </Grid>
                        </Grid>
                        <InfoText text={translate('orders:EXTERNAL_USER_BATCH_APPROVED_BY_INSTRUCTIONS')} />
                    </Box>

                    <Box mt={2}>
                        <Typography variant="h4">{translate('orders:INVOICE_STATE')}</Typography>

                        {counts.COLLECTIVE ? (
                            <Box mt={1} data-test="external-user-batch-send-to-clinic-dialog.collective-section">
                                <Typography variant="subtitle2">
                                    {translate('orders:BATCH_ORDERS_WITH_COLLECTIVE_INVOICE', {
                                        count: counts.COLLECTIVE,
                                    })}
                                </Typography>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        value={collectiveInvoiceAction}
                                        onChange={(event) =>
                                            setCollectiveInvoiceAction(event.target.value as ExternalUserInvoiceAction)
                                        }
                                    >
                                        <FormControlLabel
                                            value={ExternalUserInvoiceAction.NOT_READY}
                                            control={<Radio color="secondary" />}
                                            label={translate('orders:NOT_READY')}
                                        />
                                        <FormControlLabel
                                            value={ExternalUserInvoiceAction.READY_FOR_INVOICE}
                                            control={<Radio color="secondary" />}
                                            label={translate('orders:READY_FOR_INVOICE')}
                                        />
                                        <FormControlLabel
                                            value={ExternalUserInvoiceAction.SHOULD_NOT_INVOICE}
                                            control={<Radio color="secondary" />}
                                            label={translate('orders:DO_NOT_INVOICE')}
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                        ) : undefined}

                        {counts.PER_ORDER ? (
                            <Box mt={1} data-test="external-user-batch-send-to-clinic-dialog.per-order-section">
                                <Alert severity="info" variant="outlined" sx={{ border: 0 }}>
                                    {translate('orders:BATCH_PER_ORDER_INVOICE_LEFT_TO_LAB', {
                                        count: counts.PER_ORDER,
                                    })}
                                </Alert>
                            </Box>
                        ) : undefined}

                        {counts.NO_INVOICE ? (
                            <Box mt={1} data-test="external-user-batch-send-to-clinic-dialog.no-invoice-section">
                                <Alert severity="info" variant="outlined" sx={{ border: 0 }}>
                                    {translate('orders:BATCH_ORDERS_NOT_INVOICED', { count: counts.NO_INVOICE })}
                                </Alert>
                            </Box>
                        ) : undefined}
                    </Box>

                    <Box mt={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={compileDeliveryNotes}
                                    onChange={(event) => setCompileDeliveryNotes(event.target.checked)}
                                    data-test="external-user-batch-send-to-clinic-dialog.compile-pdf-checkbox"
                                />
                            }
                            label={translate('orders:CREATE_COMPILED_DELIVERY_NOTES_PDF')}
                        />
                    </Box>
                </Box>
            )}
        </DHDialog>
    );
};

export default ExternalUserBatchSendToClinicDialog;
